import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField } from "@mui/material"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { StatusesProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Statuses } from "assets/SideBarIcons/status.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useState } from "react";
import Loader from "layout/Loader";
import { GetStatusByIdResponse } from "subModule/src/services/statuses/interface";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData { [key: string]: any;}
export default function StatusesModal() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const statusType = params?.typename === 'packages' ? 1 : params?.typename === 'services' ? 2 : 3;
    const { enqueueSnackbar } = useSnackbar();
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const { register, control, handleSubmit, formState: { errors, dirtyFields, isDirty }, reset } = useForm<FormData>({defaultValues: {}});
    const loggedInUserData = useSelector(selectUserInfo);
    const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
    const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
    const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(params?.id ? false : true);
    const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
        sectionLabelKey: "general",
        moduleLabelKey: "Statuses",
        moduleHref: `/status/${params.typename}`,
        entityFallbackLabelKey: "name",
        isEdit: !!params?.id,
    });
    useEffect(() => {
        if(params?.id) {
            if(loading) getByID();
        }
    }, [params]);
    useEffect(() => {
        if (isDirty && params?.id) setIsBtnEnabled(true);
      }, [isDirty, dirtyFields]);
    const getByID = () => {
        StatusesProfileService.getStatusByID(+(params as any)?.id, {type: statusType}).then((res: GetStatusByIdResponse) => {
            if(res.success) {
                reset({ ...res?.data || {} });
                captureLabel(res?.data?.name);
                setLoading(false);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err:any) => { console.error("===", err); });
    }
    const onSubmit = (data: any) => {
        setIsBtnEnabled(false);
        if(params?.id) {
            StatusesProfileService.updateStatuses(+params?.id, data).then((response: any) => {
                if(response.success) {
                    captureLabel(data?.name);
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('status') + ' ' + t('updated') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    enqueueSnackbar(<AlertMessages text={t(response?.message)} />, { variant: 'error' });
                }
                setIsBtnEnabled(true);
            }).catch((err:any) => { 
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        } else {
            StatusesProfileService.createStatuses({...data, type: statusType}).then((response: any) => {
                if(response.success) {
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('status') + ' ' + t('created') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    setIsBtnEnabled(true);
                    enqueueSnackbar(<AlertMessages text={t(response?.message)} />, { variant: 'error' });
                }
            }).catch((err:any) => { 
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        }
    };
    return (<>
        <ComponentHeaderTwo
            showBtn={true}
            showSaveBtn={true}
            onClick={() => navigate(-1)}
            onSubmit={handleSubmit(onSubmit)}
            heading={params?.id ? t('editStatuses') : t('addStatuses')}
            icon={<Statuses height={28} width={28} />}
            btnText={"back"}
            btnType={"back"}
            isBtnEnabled={IsBtnEnabled}
            showCreateBtn={checkPrivileges("ROLE_BUSINESSSTATUS_CREATE")}
            breadcrumbItems={breadcrumbItems}
        />
        {loading ? <Loader /> : <SimpleModal
            visable={true}
            title={params?.id ? "editStatuses" : "addStatuses"}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={ 
                <Grid container component="form" id="form" spacing={3} onSubmit={handleSubmit(onSubmit)}>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            {...register("name", { validate: { required: (value) => value.trim() !== '' || t('This field is required.'), } })} 
                            label={`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`}
                            placeholder={t('phStatusNameEn')}
                            title={(`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`).replace(/\s+/g, ' ').trim()}
                            fullWidth
                            error={!!errors.name} 
                            helperText={errors.name?.message as string} 
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            {...register("description")} 
                            label={t('description')}
                            placeholder={t('phStatusDescription')}
                            title={t('description')}
                            fullWidth
                            error={!!errors.description} 
                            helperText={errors.description?.message as string}
                            InputLabelProps={{ shrink: true }}
                            multiline
                            rows={1}
                            sx={{ 
                                '& .MuiInputBase-fullWidth.MuiInputBase-multiline': { width: '100%', paddingRight: '0px', paddingBottom: 0 },
                                '& .MuiInputBase-inputMultiline': { resize: 'vertical', minHeight: '29px' }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField 
                            type="number"
                            {...register("sortOrder")} 
                            onChange={(e: any) => {
                                const value = parseInt(e.target.value);
                                e.target.value = value < 1 ? 1 : value > 100 ? 100 : value || "";
                                if(!IsBtnEnabled) setIsBtnEnabled(true);
                            }}
                            label={t("Sort Order (Value 1-100)")}
                            placeholder={t("phSortOrder")}
                            title={t("Sort Order (Value 1-100)")}
                            fullWidth
                            error={!!errors.sortOrder} 
                            helperText={errors.sortOrder?.message as string} 
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    {checkPrivileges('ROLE_MANAGE_DEFAULT_BUSINESSSTATUS') ? <Grid item xs={12} md={3}>
                        <Controller
                            name="default"
                            control={control}
                            defaultValue={false}
                            render={({ field }) => (
                                <CommonCheckbox
                                    {...field}
                                    label={t("isDefault")}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
                                        checked={field.value}
                                        color={themeColors?.primary}
                                />
                            )}
                        />
                    </Grid> : null}
                </Grid>
            }
        />}
    </>)
}